
import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  maxRating?: number;
  minValue: number;
  maxValue: number;
  onMinChange?: (value: number) => void;
  onMaxChange?: (value: number) => void;
  className?: string;
}

export function StarRating({
  maxRating = 5,
  minValue = 0,
  maxValue = 5,
  onMinChange,
  onMaxChange,
  className,
}: StarRatingProps) {
  const handleMinStarClick = (value: number) => {
    if (onMinChange) {
      const newValue = minValue === value ? 0 : value;
      onMinChange(newValue);
    }
  };

  const handleMaxStarClick = (value: number) => {
    if (onMaxChange) {
      const newValue = maxValue === value ? 5 : value;
      onMaxChange(newValue);
    }
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center gap-1">
        <div className="text-sm min-w-16 text-muted-foreground">Da:</div>
        <div className="flex">
          {Array.from({ length: maxRating }).map((_, i) => {
            const starValue = i + 1;
            return (
              <button
                key={`min-${i}`}
                onClick={() => handleMinStarClick(starValue)}
                className="focus:outline-none p-1"
                aria-label={`Set minimum rating to ${starValue}`}
              >
                <Star
                  className={cn(
                    "h-6 w-6 transition-all",
                    starValue <= minValue
                      ? "text-gold fill-gold"
                      : "text-muted stroke-1"
                  )}
                />
              </button>
            );
          })}
          <span className="ml-2 text-sm self-center font-medium">{minValue}</span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <div className="text-sm min-w-16 text-muted-foreground">A:</div>
        <div className="flex">
          {Array.from({ length: maxRating }).map((_, i) => {
            const starValue = i + 1;
            return (
              <button
                key={`max-${i}`}
                onClick={() => handleMaxStarClick(starValue)}
                className="focus:outline-none p-1"
                aria-label={`Set maximum rating to ${starValue}`}
              >
                <Star
                  className={cn(
                    "h-6 w-6 transition-all",
                    starValue <= maxValue
                      ? "text-gold fill-gold"
                      : "text-muted stroke-1"
                  )}
                />
              </button>
            );
          })}
          <span className="ml-2 text-sm self-center font-medium">{maxValue}</span>
        </div>
      </div>
    </div>
  );
}
